
import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Crop } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { storiesService } from "@/services/storiesService";

interface StoryGifCropperProps {
  isOpen: boolean;
  onClose: () => void;
  story: {
    id: string;
    media_url: string;
    duration: number;
  };
  onGifCreated: () => void;
}

const StoryGifCropper = ({ isOpen, onClose, story, onGifCreated }: StoryGifCropperProps) => {
  const [startTime, setStartTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 300, height: 300 });
  const [isGenerating, setIsGenerating] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cropRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Maximum allowed start time (story duration - 5 seconds)
  const maxStartTime = Math.max(0, story.duration - 5);

  const handleTimeChange = (value: number[]) => {
    const newStartTime = value[0];
    setStartTime(newStartTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newStartTime;
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        // Play only the 5-second segment
        videoRef.current.currentTime = startTime;
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle video time update to loop the 5-second segment
  const handleTimeUpdate = () => {
    if (videoRef.current && isPlaying) {
      if (videoRef.current.currentTime >= startTime + 5) {
        videoRef.current.currentTime = startTime;
      }
    }
  };

  // Initialize crop area when video loads
  useEffect(() => {
    if (videoRef.current && isOpen) {
      const video = videoRef.current;
      video.onloadeddata = () => {
        const videoRect = video.getBoundingClientRect();
        const size = Math.min(videoRect.width, videoRect.height) * 0.8;
        setCropArea({
          x: (videoRect.width - size) / 2,
          y: (videoRect.height - size) / 2,
          width: size,
          height: size
        });
      };
    }
  }, [isOpen]);

  const generateGif = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsGenerating(true);
    
    try {
      // This is a simplified GIF generation - in production, you'd use a library like gif.js
      // For now, we'll create a mock GIF blob
      const mockGifBlob = new Blob(['mock gif data'], { type: 'image/gif' });
      
      const cropData = {
        start_time: startTime,
        crop: {
          x: cropArea.x,
          y: cropArea.y,
          width: cropArea.width,
          height: cropArea.height
        }
      };

      await storiesService.uploadGifPreview(story.id, mockGifBlob, cropData);

      toast({
        title: "GIF Created!",
        description: "Your profile GIF has been generated and saved",
      });

      onGifCreated();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate GIF",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg mx-auto bg-carbon border-electric-violet">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-ice-white text-center">
            Create Profile GIF
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video Preview with Crop Overlay */}
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              src={story.media_url}
              className="w-full h-64 object-cover"
              muted
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setIsPlaying(false)}
            />
            
            {/* Crop Overlay */}
            <div
              ref={cropRef}
              className="absolute border-2 border-electric-violet bg-electric-violet/20"
              style={{
                left: `${cropArea.x}px`,
                top: `${cropArea.y}px`,
                width: `${cropArea.width}px`,
                height: `${cropArea.height}px`,
                pointerEvents: 'none'
              }}
            >
              <div className="absolute top-1 left-1 text-xs text-electric-violet bg-carbon/80 px-1 rounded">
                GIF Preview
              </div>
            </div>
          </div>

          {/* Time Selection */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-ice-white/70">Select 5-second segment:</span>
              <span className="text-sm text-electric-violet">
                {startTime.toFixed(1)}s - {(startTime + 5).toFixed(1)}s
              </span>
            </div>
            <Slider
              value={[startTime]}
              onValueChange={handleTimeChange}
              max={maxStartTime}
              step={0.1}
              className="w-full"
            />
          </div>

          {/* Controls */}
          <div className="flex justify-center space-x-4">
            <Button
              variant="outline"
              size="icon"
              onClick={togglePlay}
              className="border-electric-violet text-electric-violet hover:bg-electric-violet/10"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          </div>

          {/* Info */}
          <div className="bg-electric-violet/10 p-3 rounded-lg border border-electric-violet/30">
            <p className="text-xs text-ice-white/70">
              • Drag the timeline to select which 5 seconds to use
              • The highlighted area shows your GIF crop region
              • This GIF will appear in Discovery while your story is active
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-2 pt-4">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 border-electric-violet text-electric-violet"
            >
              Cancel
            </Button>
            <Button 
              onClick={generateGif} 
              disabled={isGenerating}
              className="flex-1 bg-electric-violet hover:bg-electric-violet/80"
            >
              {isGenerating ? "Generating..." : (
                <>
                  <Crop className="w-4 h-4 mr-2" />
                  Create GIF
                </>
              )}
            </Button>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
};

export default StoryGifCropper;
